import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  RiAddLine,
  RiEdit2Line,
  RiDeleteBin6Line,
  RiSearchLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
} from "react-icons/ri";

import { getUnits, deleteUnit } from "../services/unitService";
import "./UnitList.css";

const UnitListLayer = () => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [pageSize, setPageSize] = useState(5); // ✅ NEW

  useEffect(() => {
    loadUnits();
  }, []);

  const loadUnits = async () => {
    try {
      const res = await getUnits();
      if (res.status === "success") {
        setUnits(res.data || []);
      } else toast.error(res.message);
    } catch {
      toast.error("API Error");
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this unit?")) return;

    try {
      const res = await deleteUnit(id);
      if (res.status === "success") {
        toast.success("Unit deleted");
        loadUnits();
      } else toast.error(res.message);
    } catch {
      toast.error("API Error");
    }
  };

  /* SEARCH */
  const filteredData = useMemo(() => {
    return units.filter((u) =>
      `${u.unit_name} ${u.description || ""}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [units, search]);

  /* PAGINATION LOGIC */
  const effectivePageSize =
    pageSize === "all" ? filteredData.length : pageSize;

  const totalPages =
    effectivePageSize === 0
      ? 1
      : Math.ceil(filteredData.length / effectivePageSize);

  const paginatedData =
    pageSize === "all"
      ? filteredData
      : filteredData.slice(
          (currentPage - 1) * effectivePageSize,
          currentPage * effectivePageSize
        );

  return (
    <div className="ul-page">
      {/* HEADER */}
      <div className="ul-header">
        <h4>Unit List</h4>
        <Link to="/add-unit" className="ul-add-btn">
          <RiAddLine /> Add Unit
        </Link>
      </div>

      {/* FILTER BAR */}
      <div className="ul-filters">
        <div className="ul-search">
          <RiSearchLine />
          <input
            placeholder="Search unit..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        {/* PAGE SIZE SELECT */}
        <div className="ul-pagesize">
          <span>Show</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(
                e.target.value === "all"
                  ? "all"
                  : Number(e.target.value)
              );
              setCurrentPage(1);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value="all">All</option>
          </select>
          <span>entries</span>
        </div>
      </div>

      {/* TABLE */}
      <div className="ul-table-wrapper">
        <table className="ul-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Unit Name</th>
              <th>Description</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" className="text-center">Loading...</td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan="4" className="text-center text-muted">
                  No units found
                </td>
              </tr>
            ) : (
              paginatedData.map((u, i) => (
                <tr key={u.id}>
                  <td>
                    {pageSize === "all"
                      ? i + 1
                      : (currentPage - 1) * effectivePageSize + i + 1}
                  </td>
                  <td>{u.unit_name}</td>
                  <td>{u.description || "-"}</td>
                  <td className="ul-actions">
                    <Link to={`/edit-unit/${u.id}`} className="ul-btn edit">
                      <RiEdit2Line />Edit
                    </Link>
                    <button
                      className="ul-btn delete"
                      onClick={() => handleDelete(u.id)}
                    >
                      <RiDeleteBin6Line />Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      {pageSize !== "all" && totalPages > 1 && (
        <div className="ul-pagination">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            <RiArrowLeftSLine />
          </button>

          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              className={currentPage === i + 1 ? "active" : ""}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            <RiArrowRightSLine />
          </button>
        </div>
      )}
    </div>
  );
};

export default UnitListLayer;
